import React, { useContext } from 'react';
import { SubmissionContext } from '../../../Context/SubmissionProvider';
import { AccordionSection } from '../../../../../Admin/Game/GameTab/utils';
import { StyledFormControlLabel } from '../../../RevenueShareForm/revenueShareForm.styles';
import { getLatestBuild } from '../../../../../../../../common/build-utils';
import { Checkbox, SxProps, Table, TableBody, TableCell, TableRow, Theme } from '@mui/material';
import { StyledBodyText } from '../../../../../../../../common/Styleguide/Common/Text';
import TermsLink from '../../../../../../../../common/components/TermsLink';
import { RevenueShareDataV1 } from '@crazygames/graph-types/revenue/revenue-share-v1.type';
import { RevenueShareDataV2 } from '@crazygames/graph-types/revenue/revenue-share-v2.type';
import { DOCS_URL } from '../../../../../../SideMenu/Menu';
import { RevenueShareDataV3 } from '@crazygames/graph-types/revenue/revenue-share-v3.type';
import PEGIRatingLink from '../../../../../../../../common/components/PEGIRatingLink';
import { formatGraphDateAndTime } from '../../../../../../../../common/formatting';

const SubmissionTabInfosCommercial: React.FC = () => {
  const { submission, game } = useContext(SubmissionContext);

  if (!submission || !submission.rawRevenueShareOptions) {
    return <>Missing revenue share data</>;
  }

  const isIframe = getLatestBuild(submission.gameBuilds).loaderType === 'iframe';

  const renderCheckbox = (value: boolean, label: string | React.JSX.Element, sx?: SxProps<Theme> | undefined) => {
    return <StyledFormControlLabel sx={{ m: 0, ...sx }} control={<Checkbox checked={value} disabled />} label={label} />;
  };

  const renderIframeRevenueShareDisabled = () => {
    return (
      <StyledBodyText color="white30">
        Revenue share is not available for games embedded via iframe. Read more about revenue share conditions in section 5.3 in our{' '}
        <TermsLink />.
      </StyledBodyText>
    );
  };

  const renderV1 = () => {
    const conditions: RevenueShareDataV1 = JSON.parse(submission!.rawRevenueShareOptions!);

    return (
      <>
        {renderCheckbox(conditions.noThirdPartyBranding, 'No third-party branding is present in the game.')}
        {renderCheckbox(conditions.exclusiveOnCrazygames, 'The game is exclusively available on CrazyGames.')}
        {renderCheckbox(conditions.notPublishedElsewhere, 'The game is not published elsewhere.')}
        {renderCheckbox(conditions.crazygamesLogoEmbedded, 'The CrazyGames logo is embedded in the game.')}
        {renderCheckbox(conditions.crazygamesRedistributionAllowed, 'Redistribution of the game is allowed by CrazyGames.')}
      </>
    );
  };

  /** v2 is similar to v3 but is also asks if the developer has implemented a logo */
  const renderV2 = () => {
    const conditions: RevenueShareDataV2 = JSON.parse(submission!.rawRevenueShareOptions!);
    const isInvited = conditions.isInvited || submission.isInvited;
    return (
      <>
        {!isInvited && (
          <>
            {renderCheckbox(true, 'I submit this game on my own initiative, without an invitation by or agreement with CrazyGames.')}
            {renderCheckbox(
              true,
              <>
                I agree to publish the games under the <TermsLink /> of the developer portal.
              </>,
              { pl: 5 },
            )}
          </>
        )}
        {isInvited && (
          <>
            {renderCheckbox(
              true,
              'I have been invited by CrazyGames to submit the game and/or my game has an agreement contract with CrazyGames. Selecting this option without an invitation will result in automatic rejection of your submission.',
            )}
            {!conditions.hasCustomContract &&
              renderCheckbox(
                true,
                <>
                  I publish the games under the <TermsLink /> of the developer portal.
                </>,
                { pl: 5 },
              )}
            {conditions.hasCustomContract &&
              renderCheckbox(
                true,
                <>
                  I have signed a custom contract with CrazyGames. If this option is mistakenly selected, I accept the general{' '}
                  <TermsLink overrideTermsVersion={submission.acceptedTermsVersion} />
                  of the developer portal.
                </>,
                { pl: 5 },
              )}
          </>
        )}
        {renderCheckbox(
          conditions.suitableForChildren,
          <>
            I confirm that my game is suitable for players aged 12+. In particular, I certify that it suits the <PEGIRatingLink /> criteria.
          </>,
        )}
        {isIframe && renderIframeRevenueShareDisabled()}
        {!isIframe && (
          <>
            {renderCheckbox(
              conditions.isEligible,
              'I read and understood article 5.3 and acknowledge that my game is eligible for revenue share.',
            )}
            {renderCheckbox(
              conditions.makeExclusive,
              'I will make this game exclusively available on CrazyGames for two months following the release date. I understand that after these two months I am free to upload my game anywhere I like.',
            )}
            {renderCheckbox(
              conditions.hasImplementedSDK,
              <>
                I implemented the CrazyGames SDK, including the in-game advertisements (
                <a href={`${DOCS_URL}/`} target="_blank" rel="noopener noreferrer">
                  SDK Documentation
                </a>
                ).
              </>,
            )}
            {renderCheckbox(conditions.hasImplementedLogo, 'I included a CrazyGames logo in game')}
          </>
        )}
      </>
    );
  };

  const renderV3 = () => {
    const conditions: RevenueShareDataV3 = JSON.parse(submission!.rawRevenueShareOptions!);
    const isInvited = submission.isInvited;
    const isFullLaunch = submission.isFullLaunch;
    return (
      <>
        <StyledBodyText variant="h2">Contractual agreement</StyledBodyText>
        {!isInvited && (
          <>
            {renderCheckbox(
              true,
              <>
                I agree to the <TermsLink overrideTermsVersion={submission.acceptedTermsVersion} /> of the Developer Portal and acknowledge
                that they apply to my game submission.
              </>,
            )}
          </>
        )}
        {isInvited &&
          !conditions.hasCustomContract &&
          renderCheckbox(
            true,
            <>
              I publish the games under the <TermsLink /> of the developer portal.
            </>,
          )}
        {isInvited &&
          conditions.hasCustomContract &&
          renderCheckbox(
            true,
            <>
              I have signed a custom contract with CrazyGames. If this option is mistakenly selected, I accept the general <TermsLink /> of
              the developer portal.
            </>,
          )}
        {renderCheckbox(
          conditions.suitableForChildren,
          <>
            I confirm that my game is appropriate for players aged 12 and older, and specifically meets the criteria outlined under the{' '}
            <PEGIRatingLink /> rating.
          </>,
        )}
        {!isIframe &&
          isFullLaunch &&
          renderCheckbox(
            conditions.makeExclusive,
            <>
              I agree to make my game exclusively available on CrazyGames for two months after release. In return, I’ll receive a 50%
              increased revenue share during this period. After two months, I’m free to publish the game elsewhere.
            </>,
          )}
        <StyledBodyText variant="h2">Additional information</StyledBodyText>
        <Table
          sx={{
            'th, td': { border: 0 },
            width: 400,
          }}
        >
          <TableBody>
            <TableRow>
              <TableCell>Submission date:</TableCell>
              <TableCell>{formatGraphDateAndTime(submission.submittedOn)}</TableCell>
            </TableRow>
            {submission.basicLaunchOn && (
              <TableRow>
                <TableCell>Basic Launch started on:</TableCell>
                <TableCell>{formatGraphDateAndTime(submission.basicLaunchOn)}</TableCell>
              </TableRow>
            )}
            {game?.addedOn && (
              <TableRow>
                <TableCell>Published date:</TableCell>
                <TableCell>{formatGraphDateAndTime(game.addedOn)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </>
    );
  };

  const renderRevenueShareVersion: () => JSX.Element | null = () => {
    if (submission.revenueShareVersion === undefined) {
      return null;
    }
    switch (submission.revenueShareVersion) {
      case 1:
        return renderV1();
      case 2:
        return renderV2();
      case 3:
        return renderV3();
    }
  };

  return (
    <AccordionSection title="Commercial Info">
      <div style={{ display: 'flex', flexDirection: 'column' }}>{renderRevenueShareVersion()}</div>
    </AccordionSection>
  );
};

export default SubmissionTabInfosCommercial;
